import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { I18nModule, Translatable } from '@spartacus/core';
import { EMPTY, Subscription, last, repeat, switchMap, takeWhile } from 'rxjs';
import { PostPlacedOrderPayload } from 'src/app/model/checkout.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { PaymentService } from 'src/app/service/payment/payment.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';

@Component({
  selector: 'app-payment-confirmation-loader',
  standalone: true,
  imports: [CommonModule, I18nModule],
  templateUrl: './payment-confirmation-loader.component.html',
  styleUrl: './payment-confirmation-loader.component.scss',
})
export class PaymentConfirmationLoaderComponent implements OnInit, OnDestroy {
  inProgress = true;
  errorMessage: Translatable;

  // Subscriptions
  loadPaymentConfirmationSubscription$: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private storageService: StorageService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private componentService: ComponentService,
    private paymentService: PaymentService
  ) {}

  ngOnInit(): void {
    // observable is used to 'reload the page' again on scenario:
    // user is in payment confirmation page and is redirected back to payment confirmation page
    this.loadPaymentConfirmationSubscription$ = this.componentService.loadPaymentConfirmation$.subscribe(
      (load: boolean) => {
        if (load) {
          const cartId = this.route.snapshot.params.cartId;
          const ref = this.route.snapshot.queryParams.ref;

          this.paymentService
            .getPaymentStatus(cartId, ref)
            .pipe(
              repeat({ delay: 5000, count: 12 }), // call API for every 5 seconds for 12 times
              takeWhile((res: any) => res.paid === false, true), // while paid is false
              last(),
              switchMap((res: any) => {
                if (res.paid === true) {
                  const array = new Uint32Array(1);
                  let payload: PostPlacedOrderPayload;

                  payload = ShimanoUtilMethod.decodeBase64ToObject(res.placeOrderRequest);
                  payload.fields = 'DEFAULT';
                  payload.cartId = cartId;
                  payload.securityCode = window.crypto.getRandomValues(array)[0].toString();

                  return this.checkoutServiceExtends.postCheckoutPlaceOrder(payload);
                } else if (res.paid === false) {
                  this.displayError(res.attemptMessage);
                  return EMPTY;
                }
              })
            )
            .subscribe({
              next: (res) => {
                this.storageService.removeSessionStorageItem('saddID');
                this.storageService.removeSessionStorageItem('saddFormatted');
                this.storageService.removeSessionStorageItem('baddID');
                this.storageService.removeSessionStorageItem('baddFormatted');
                const goToUrl = '/order-confirmation?code=';
                this.router.navigateByUrl(`${goToUrl}${res.code}`);
              },
              error: (err) => {
                this.displayError(err);
              },
            });
        }
      }
    );
  }

  displayError(error: any): void {
    // check data type of error
    switch (typeof error) {
      case 'string':
        this.errorMessage = { raw: error };
        break;
      case 'object':
        if (this.errorMessageIsKey(error.error.errors[0].message)) {
          this.errorMessage = { key: error.error.errors[0].message };
        } else {
          this.errorMessage = { raw: error.error.errors[0].message };
        }
        break;
      default:
        break;
    }
    this.inProgress = false;
    document.querySelector('div.PaymentConfirmationText')?.classList.add('d-none');
    this.cdr.detectChanges();
  }

  errorMessageIsKey(message: string): boolean {
    return message.startsWith('shimano.');
  }

  ngOnDestroy(): void {
    this.loadPaymentConfirmationSubscription$?.unsubscribe();
  }
}
